<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-heart me-2"></i>{{ $t('page.ntutu', 'Ntutu Care — Narok County Cover') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- HERO BANNER -->
      <div class="hms-ntutu-hero mb-3">
        <div>
          <div class="hms-hero-tagline">"County leadership in Universal Health Coverage"</div>
          <div class="hms-hero-name">Ntutu Care</div>
          <div class="hms-hero-sub">A county-government flagship — paying for care for our most vulnerable: pregnant mothers, under-fives, the elderly (≥65), widows, orphans, persons with severe disability, and indigents.</div>
        </div>
        <div class="hms-hero-stats">
          <div><strong>{{ stats.active_enrolees || 0 }}</strong><span>Active members</span></div>
          <div><strong>{{ stats.new_30d || 0 }}</strong><span>New (30d)</span></div>
          <div><strong>KSh {{ formatNumber(claimStats.approved_value || 0) }}</strong><span>Approved 90d</span></div>
        </div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='enrolees' }" @click="tab='enrolees'"><i class="mdi mdi-account-multiple me-1"></i>Members</button>
        <button class="hms-tab" :class="{ active: tab==='claims' }" @click="tab='claims'; loadClaims()"><i class="mdi mdi-clipboard-check me-1"></i>Claims</button>
        <button class="hms-tab" :class="{ active: tab==='benefits' }" @click="tab='benefits'"><i class="mdi mdi-list-box me-1"></i>Benefit Schedule</button>
      </div>

      <!-- ============ ENROLEES ============ -->
      <div v-if="tab==='enrolees'">
        <div class="hms-cat-row mb-3">
          <div v-for="c in categories" :key="c.code" class="hms-cat" :class="{ active: catFilter === c.code }" @click="catFilter = (catFilter === c.code ? '' : c.code); loadAll()">
            <i :class="c.icon"></i>
            <div>
              <div class="hms-cat-num">{{ stats[c.statKey] || 0 }}</div>
              <div class="hms-cat-lbl">{{ c.label }}</div>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <input v-model="search" class="hms-input" placeholder="Search by Ntutu # / National ID / patient name" @keyup.enter="loadAll" style="max-width:380px" />
          <button class="hms-btn hms-btn-success" @click="openEnrolModal()"><i class="mdi mdi-account-plus"></i> Enrol Member</button>
        </div>
        <table class="hms-table">
          <thead>
            <tr>
              <th>Ntutu #</th><th>Member</th><th>Category</th><th>National ID</th>
              <th>Enrolled</th><th>Cover used</th><th>Facility</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in enrolees" :key="e.enrolee_id">
              <td><strong>{{ e.ntutu_number }}</strong></td>
              <td>{{ e.patient_name }}<div class="hms-muted-sm">{{ e.patient_no }} · {{ e.phone || '' }}</div></td>
              <td><span class="hms-pill" :class="catPill(e.category)">{{ formatCategory(e.category) }}</span></td>
              <td>{{ e.national_id || '—' }}</td>
              <td>{{ e.enrolled_date }}</td>
              <td>
                <div>{{ e.claims_used }} claims</div>
                <div class="hms-muted-sm">KSh {{ formatNumber(e.amount_used || 0) }}</div>
              </td>
              <td>{{ e.enrolled_facility }}</td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openClaimModal(e)"><i class="mdi mdi-clipboard-plus"></i> Claim</button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ CLAIMS ============ -->
      <div v-if="tab==='claims'">
        <div class="d-flex justify-content-between mb-2">
          <select v-model="claimStatusFilter" class="hms-input" @change="loadClaims" style="max-width:240px">
            <option value="">All statuses</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="partially_approved">Partially approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        <table class="hms-table">
          <thead>
            <tr>
              <th>Claim #</th><th>Member</th><th>Benefit</th><th>Service date</th>
              <th>Claimed</th><th>Approved</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in claims" :key="c.claim_id">
              <td><strong>{{ c.claim_no }}</strong></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.ntutu_number }}</div></td>
              <td>{{ c.benefit_code }}<div class="hms-muted-sm">{{ c.benefit_name }}</div></td>
              <td>{{ c.service_date }}</td>
              <td>KSh {{ formatNumber(c.claim_amount_kes) }}</td>
              <td>KSh {{ formatNumber(c.approved_amount_kes || 0) }}</td>
              <td><span class="hms-pill" :class="claimStatusPill(c.status)">{{ formatClaimStatus(c.status) }}</span></td>
              <td><button v-if="c.status === 'submitted'" class="hms-btn hms-btn-success hms-btn-sm" @click="openDecisionModal(c)"><i class="mdi mdi-gavel"></i> Decide</button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ BENEFITS ============ -->
      <div v-if="tab==='benefits'">
        <div class="hms-card">
          <div class="hms-card-head">Ntutu Care benefit schedule — {{ benefits.length }} packages</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead><tr><th>Code</th><th>Package</th><th>Category</th><th>Scope</th><th>Ceiling</th><th>Referral?</th></tr></thead>
              <tbody>
                <tr v-for="b in benefits" :key="b.benefit_id">
                  <td><strong>{{ b.code }}</strong></td>
                  <td>{{ b.name }}</td>
                  <td><span class="hms-pill" :class="catPill(b.category)">{{ formatCategory(b.category) }}</span></td>
                  <td class="hms-muted-sm">{{ b.service_scope }}</td>
                  <td>KSh {{ formatNumber(b.ceiling_kes) }}</td>
                  <td><span v-if="b.requires_referral == 1" class="hms-pill hms-pill-warn">Yes</span><span v-else class="hms-muted-sm">No</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== ENROL MODAL ===== -->
    <div v-if="enrolModal" class="hms-modal-bg" @click.self="enrolModal=false">
      <div class="hms-modal" style="width:600px">
        <div class="hms-modal-head"><h5>Enrol on Ntutu Care</h5><button class="hms-icon-btn" @click="enrolModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div v-if="!enrol.patient_id">
            <label class="hms-label">Patient *</label>
            <div class="d-flex gap-2">
              <input v-model="enrol.patient_search" class="hms-input" placeholder="Name / patient # / phone" @keyup.enter="findEnrolPatient" />
              <button class="hms-btn hms-btn-blue" @click="findEnrolPatient">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip"><i class="mdi mdi-account"></i> {{ enrol.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="enrol.patient_id=null">change</a></div>
          <div class="row g-2 mt-2">
            <div class="col-6"><label class="hms-label">Category *</label>
              <select v-model="enrol.category" class="hms-input">
                <option value="mch">MCH (pregnant / postnatal)</option>
                <option value="child_under5">Child under 5</option>
                <option value="elderly">Elderly (≥65)</option>
                <option value="widow">Widow</option>
                <option value="orphan">Orphan (under 18)</option>
                <option value="pwd_severe">Person with severe disability (NCPWD Cat 3/4)</option>
                <option value="pwd">Person with disability</option>
                <option value="chronic_disease">Chronic disease</option>
                <option value="indigent">Indigent</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Annual ceiling (KSh)</label><input v-model.number="enrol.cover_ceiling_kes" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">National ID</label><input v-model="enrol.national_id" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Birth certificate</label><input v-model="enrol.birth_certificate" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Enrolled date *</label><input v-model="enrol.enrolled_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Expiry date</label><input v-model="enrol.expiry_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Verified by</label><input v-model="enrol.verified_by" class="hms-input" placeholder="Chief / CHV / facility i/c" /></div>
            <div class="col-6"><label class="hms-label">Verification doc</label><input v-model="enrol.verification_doc" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV name</label><input v-model="enrol.chv_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV phone</label><input v-model="enrol.chv_phone" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="enrol.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="enrolModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !enrol.patient_id" @click="saveEnrol">{{ saving?'Enrolling…':'Enrol' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== CLAIM MODAL ===== -->
    <div v-if="claimModal" class="hms-modal-bg" @click.self="claimModal=false">
      <div class="hms-modal" style="width:600px">
        <div class="hms-modal-head"><h5>Submit Ntutu Claim</h5><button class="hms-icon-btn" @click="claimModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner"><strong>{{ claimEnrolee.patient_name }}</strong> · {{ claimEnrolee.ntutu_number }} · {{ formatCategory(claimEnrolee.category) }}</div>
          <div class="row g-2">
            <div class="col-12"><label class="hms-label">Benefit *</label>
              <select v-model.number="claim.benefit_id" class="hms-input">
                <option :value="null">— select —</option>
                <option v-for="b in eligibleBenefits" :key="b.benefit_id" :value="b.benefit_id">{{ b.code }} — {{ b.name }} (ceiling KSh {{ formatNumber(b.ceiling_kes) }})</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Service date *</label><input v-model="claim.service_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Amount claimed (KSh) *</label><input v-model.number="claim.claim_amount_kes" type="number" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Service summary</label><textarea v-model="claim.service_summary" class="hms-input" rows="3" placeholder="e.g. SVD + 2 night stay; Vit K, BCG given"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="claimModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !claim.benefit_id" @click="saveClaim">{{ saving?'Submitting…':'Submit Claim' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== DECISION MODAL ===== -->
    <div v-if="decisionModal" class="hms-modal-bg" @click.self="decisionModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Decide claim — {{ decisionClaim.claim_no }}</h5><button class="hms-icon-btn" @click="decisionModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner">
            <strong>{{ decisionClaim.patient_name }}</strong> · {{ decisionClaim.benefit_code }}<br>
            Claimed: KSh {{ formatNumber(decisionClaim.claim_amount_kes) }} · Ceiling: KSh {{ formatNumber(decisionClaim.ceiling_kes) }}
          </div>
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Decision *</label>
              <select v-model="decision.status" class="hms-input">
                <option value="approved">Approved (full)</option>
                <option value="partially_approved">Partially approved</option>
                <option value="rejected">Rejected</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Approved amount (KSh)</label><input v-model.number="decision.approved_amount_kes" type="number" class="hms-input" /></div>
            <div class="col-12" v-if="decision.status === 'paid'"><label class="hms-label">Payment reference</label><input v-model="decision.payment_ref" class="hms-input" placeholder="MPESA / EFT ref" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="decision.decision_notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="decisionModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveDecision">{{ saving?'Saving…':'Save Decision' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "NtutuCare",
  components: { Navigation },
  data() {
    return {
      tab: 'enrolees', saving: false,
      stats: {}, claimStats: {},
      enrolees: [], claims: [], benefits: [],

      search: '', catFilter: '', claimStatusFilter: '',

      categories: [
        { code: 'mch',             label: 'MCH',          icon: 'mdi mdi-mother-heart',    statKey: 'mch' },
        { code: 'child_under5',    label: 'Under 5',      icon: 'mdi mdi-baby-face',       statKey: 'u5' },
        { code: 'elderly',         label: 'Elderly',      icon: 'mdi mdi-account-tie',     statKey: 'elderly' },
        { code: 'pwd',             label: 'PWD',          icon: 'mdi mdi-wheelchair',      statKey: 'pwd' },
        { code: 'chronic_disease', label: 'Chronic',      icon: 'mdi mdi-heart-pulse',     statKey: 'cd' },
        { code: 'indigent',        label: 'Indigent',     icon: 'mdi mdi-account-question',statKey: 'indigent' }
      ],

      enrolModal: false, enrol: this.blankEnrol(),
      claimModal: false, claim: this.blankClaim(), claimEnrolee: {},
      decisionModal: false, decision: this.blankDecision(), decisionClaim: {}
    };
  },
  computed: {
    eligibleBenefits() {
      if (!this.claimEnrolee.category) return this.benefits;
      const cat = this.claimEnrolee.category;
      const map = { mch: 'mch', child_under5: 'child_health', elderly: 'elderly', pwd: 'pwd', chronic_disease: 'chronic_disease', indigent: 'indigent' };
      const allowed = map[cat];
      return this.benefits.filter(b => b.category === allowed || b.category === 'any' || b.category === 'emergency');
    }
  },
  mounted() { this.loadAll(); this.loadBenefits(); },
  methods: {
    blankEnrol() { return { patient_id: null, patient_name: '', patient_search: '', national_id: '', birth_certificate: '', category: 'mch', enrolled_date: new Date().toISOString().substring(0,10), expiry_date: '', cover_ceiling_kes: 50000, verified_by: '', verification_doc: '', chv_name: '', chv_phone: '', notes: '' }; },
    blankClaim() { return { benefit_id: null, service_date: new Date().toISOString().substring(0,10), claim_amount_kes: null, service_summary: '', visit_id: '', admission_id: '', delivery_id: '' }; },
    blankDecision() { return { status: 'approved', approved_amount_kes: null, payment_ref: '', decision_notes: '' }; },

    async loadAll() {
      try {
        const r = await executeJson({ function: 'getNtutuEnrolees', search: this.search, category: this.catFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.enrolees = d.Enrolees || [];
          this.stats = d.Stats || {};
          this.claimStats = d.ClaimStats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadBenefits() {
      try {
        const r = await executeJson({ function: 'getNtutuBenefits' }, hms);
        if (r.data && r.data.success) this.benefits = (r.data.data && r.data.data.Benefits) || [];
      } catch (e) { console.error(e); }
    },
    async loadClaims() {
      try {
        const r = await executeJson({ function: 'getNtutuClaims', status: this.claimStatusFilter }, hms);
        if (r.data && r.data.success) this.claims = (r.data.data && r.data.data.Claims) || [];
      } catch (e) { console.error(e); }
    },

    openEnrolModal() { this.enrol = this.blankEnrol(); this.enrolModal = true; },
    async findEnrolPatient() {
      const q = (this.enrol.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.enrol.patient_id = p.patient_id;
        this.enrol.patient_name = p.first_name + ' ' + p.last_name;
      }
    },
    async saveEnrol() {
      this.saving = true;
      try {
        const params = { function: 'enrolNtutuMember', ...this.enrol };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.enrolModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openClaimModal(e) {
      this.claimEnrolee = e;
      this.claim = this.blankClaim();
      this.claimModal = true;
    },
    async saveClaim() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'submitNtutuClaim', enrolee_id: this.claimEnrolee.enrolee_id, ...this.claim }, hms);
        if (r.data && r.data.success) { this.claimModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openDecisionModal(c) {
      this.decisionClaim = c;
      this.decision = { ...this.blankDecision(), approved_amount_kes: c.claim_amount_kes };
      this.decisionModal = true;
    },
    async saveDecision() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'decideNtutuClaim', claim_id: this.decisionClaim.claim_id, ...this.decision }, hms);
        if (r.data && r.data.success) { this.decisionModal = false; await this.loadClaims(); await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    formatCategory(c) {
      return ({ mch: 'MCH', child_under5: 'Under 5', elderly: 'Elderly (≥65)',
                widow: 'Widow', orphan: 'Orphan', pwd_severe: 'Severe PWD', pwd: 'PWD',
                chronic_disease: 'Chronic disease', indigent: 'Indigent', child_health: 'Child health',
                emergency: 'Emergency', any: 'Any' })[c] || c;
    },
    catPill(c) {
      return ({ mch: 'hms-pill-info', child_under5: 'hms-pill-info', child_health: 'hms-pill-info',
                elderly: 'hms-pill-warn', pwd_severe: 'hms-pill-danger', pwd: 'hms-pill-warn',
                widow: 'hms-pill-warn', orphan: 'hms-pill-warn',
                chronic_disease: 'hms-pill-warn', indigent: 'hms-pill-muted',
                emergency: 'hms-pill-danger', any: 'hms-pill-success' })[c] || 'hms-pill-muted';
    },
    formatClaimStatus(s) {
      return ({ submitted: 'Submitted', approved: 'Approved', partially_approved: 'Partial',
                rejected: 'Rejected', paid: 'Paid', draft: 'Draft' })[s] || s;
    },
    claimStatusPill(s) {
      return ({ submitted: 'hms-pill-info', approved: 'hms-pill-success', partially_approved: 'hms-pill-warn',
                rejected: 'hms-pill-danger', paid: 'hms-pill-success', draft: 'hms-pill-muted' })[s] || '';
    },
    formatNumber(n) { const x = parseInt(n || 0); return x.toLocaleString(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-ntutu-hero {
  background: linear-gradient(110deg, #065f46 0%, #047857 50%, #b45309 100%);
  color: #fff; padding: 22px 28px; border-radius: 12px;
  display: flex; justify-content: space-between; align-items: center; gap: 20px;
}
.hms-hero-tagline { font-size: 12px; opacity: .8; letter-spacing: 1px; text-transform: uppercase; }
.hms-hero-name    { font-size: 28px; font-weight: 800; margin: 4px 0; }
.hms-hero-sub     { font-size: 13px; opacity: .9; max-width: 600px; }
.hms-hero-stats   { display: flex; gap: 24px; }
.hms-hero-stats > div { text-align: center; }
.hms-hero-stats strong { display: block; font-size: 22px; font-weight: 800; }
.hms-hero-stats span   { font-size: 11px; opacity: .8; text-transform: uppercase; letter-spacing: .5px; }

.hms-cat-row { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; }
@media (max-width: 992px) { .hms-cat-row { grid-template-columns: repeat(3, 1fr); } }
.hms-cat { display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; cursor: pointer; }
.hms-cat:hover { background: #f7fafc; }
.hms-cat.active { border-color: #38a169; background: #f0fff4; }
.hms-cat .mdi { font-size: 22px; color: #4a5568; }
.hms-cat-num   { font-weight: 700; color: #1a202c; font-size: 18px; }
.hms-cat-lbl   { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #047857; border-bottom-color: #047857; font-weight: 600; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body.p-0 { padding: 0; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 8px 10px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #047857; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }

.hms-muted-sm { color: #a0aec0; font-size: 11px; }
</style>
